import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'clearcommunication_daily_views';

const FreePhraseContext = createContext();

const getTodayKey = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

export const FreePhraseProvider = ({ children }) => {
  const [dailyData, setDailyData] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        if (data.date !== getTodayKey()) {
          return { date: getTodayKey(), viewedIds: [] };
        }
        return data;
      }
      return { date: getTodayKey(), viewedIds: [] };
    } catch {
      return { date: getTodayKey(), viewedIds: [] };
    }
  });

  useEffect(() => {
    const checkAndReset = () => {
      if (dailyData.date !== getTodayKey()) {
        setDailyData({ date: getTodayKey(), viewedIds: [] });
      }
    };
    checkAndReset();
    const interval = setInterval(checkAndReset, 60000);
    return () => clearInterval(interval);
  }, [dailyData.date]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dailyData));
  }, [dailyData]);

  // No limits — all phrases are free. Just track views for stats.
  const canViewPhrase = useCallback(() => true, []);

  const markPhraseViewed = useCallback((phraseId) => {
    setDailyData(prev => {
      if (prev.date !== getTodayKey()) {
        return { date: getTodayKey(), viewedIds: [phraseId] };
      }
      if (prev.viewedIds.includes(phraseId)) {
        return prev;
      }
      return { ...prev, viewedIds: [...prev.viewedIds, phraseId] };
    });
  }, []);

  const isLimitReached = false; // No limits
  const remainingFreeViews = 999;

  const resetViewedPhrases = useCallback(() => {
    setDailyData({ date: getTodayKey(), viewedIds: [] });
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const viewedPhraseIds = dailyData.viewedIds;

  const value = {
    viewedPhraseIds,
    canViewPhrase,
    markPhraseViewed,
    isLimitReached,
    remainingFreeViews,
    resetViewedPhrases,
    FREE_PHRASE_LIMIT: 999,
    isDaily: false
  };

  return (
    <FreePhraseContext.Provider value={value}>
      {children}
    </FreePhraseContext.Provider>
  );
};

export const useFreePhrase = () => {
  const context = useContext(FreePhraseContext);
  if (!context) {
    throw new Error('useFreePhrase must be used within a FreePhraseProvider');
  }
  return context;
};

export default FreePhraseContext;
